/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import DoneIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import FailIcon from '@material-ui/icons/NotInterested';
import StartIcon from '@material-ui/icons/PlayArrow';
import cloneDeep from 'lodash.clonedeep';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {GroupActionStatus} from './BulkEditGroupUtils';

// defines the GroupActionList prop definitions ...
export interface GroupActionListProps {
  actionArray?: GenericObject[];
  setActionArray?: Function;
  submitError?: boolean;
};

const useStyles = makeStyles(theme => ({
  rightContainer : {
    flex   : 1,
    margin : '10px 0 10px 25px'
  },
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '10px 0'
  },
  actionContainer : {
    overflowY : 'auto',
    height    : '400px'
  },
  actionText : {
    width        : '350px',
    textOverflow : 'ellipsis',
    overflow     : 'hidden'
  },
  noActions : {
    textAlign : 'center',
    marginTop : '100px',
    color     : dlsColor('grey4')
  },
  actionError : {
    marginTop      : '100px',
    display        : 'flex',
    justifyContent : 'center',
    color          : dlsColor('caRed')
  }
}));

const style = {
  actionPaper : {
    borderRadius : '6px',
    height       : 'auto',
    margin       : '10px',
    display      : 'flex',
    alignItems   : 'center',
    padding      : '10px',
    boxShadow    : 'rgba(0, 0, 0, 0.16) 0px 3px 10px'
  }
};

/**
 * Defines the GroupActionList component. This component display the
 * all action list for add/remove/replace groups
 */
export const GroupActionList = ({
  actionArray,
  setActionArray,
  submitError = false
}: GroupActionListProps) => {
  const classes = useStyles();

  const handleDeleteAction = useCallback((index) => {
    if (index > -1 && actionArray?.length) {
      const actionList = cloneDeep(actionArray);
      actionList.splice(index, 1);
      setActionArray(actionList);
    }
  }, [actionArray]);

  const getActionIcon = useCallback((status, index) => {
    switch (status) {
      case GroupActionStatus.Hold :
        return <CloseIcon style={{cursor: 'pointer'}} onClick={function() { handleDeleteAction(index); }}/>;
      case GroupActionStatus.Start :
        return <StartIcon style={{color: dlsColor('caBlue')}}/>;
      case GroupActionStatus.InProgress :
        return <CircularProgress color='primary' size={20} thickness={2}/>;
      case GroupActionStatus.Success :
        return <DoneIcon style={{color: dlsColor('caGreen')}}/>;
      case GroupActionStatus.Failed :
        return <FailIcon style={{color: dlsColor('caRed')}}/>;
    }
  }, [actionArray]);

  return (
    <div className={classes.rightContainer}>
      <>
        <h1 className={classes.formSectionTitle}>
          {translate('admin-groups:edit.bulk.actionList')}
        </h1>
        {actionArray?.length > 0 ? (
          <div className={classes.actionContainer}>
            {
              actionArray?.map((action, ind) => {
                return <Paper key={ind} zDepth={2} style={style.actionPaper}>
                  <div className={classes.actionText}>{action?.actionText}</div>
                  <div>{getActionIcon(action?.status, ind)}</div>
                </Paper>;
              })
            }
          </div>
        ) : (
          <div className={submitError ? classes.actionError : classes.noActions}>
            {submitError
              ? translate('admin-groups:edit.bulk.submitError')
              : translate('admin-groups:edit.bulk.noActions')
            }
          </div>
        )}
      </>
    </div>
  );
};
